import cloneDeep from 'lodash/cloneDeep';
import { useFetchWgUsersList } from 'pages/WellGuardian/WIR/hooks';
import { NOT_APPLICABLE } from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { useCountryState, useWIRUsers } from 'redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { AssetType, WIRTransverseRoles, WIRUser } from 'types/wellGuardian';

import { UserMode } from '../userRoleSettingsModal.constants';

const WIR_USERS_PAGE_SIZE = 10;

const ALL_WIR = 'ALL';

export const useUserRoleSettingsModalData = () => {
  const intl = useIntl();
  const [page, setPage] = useState<number>(1);
  const [openUserRoleSettingsModal, setOpenUserRoleSettingsModal] = useState<boolean>(false);
  const [wirUsersForPage, setWirUsersForPage] = useState<WIRUser[]>([]);
  const [openUserDetailsModal, setOpenUserDetailsModal] = useState<boolean>(false);
  const [assetTypeFilter, setAssetTypeFilter] = useState<string>(ALL_WIR);
  const [roleTypeFilter, setRoleTypeFilter] = useState<string>(NOT_APPLICABLE);
  const [openRemoveUserConfirmationModal, setOpenRemoveUserConfirmationModal] =
    useState<boolean>(false);
  const [user, setUser] = useState<WIRUser>({});
  const [mode, setMode] = useState<UserMode>();
  const countryData = useCountryState();

  const allWirUsers = useWIRUsers();
  const { usersLoading, fetchUsersList } = useFetchWgUsersList();

  const allWirUsersFiltered = useMemo(
    () =>
      cloneDeep(allWirUsers)
        .filter(item =>
          assetTypeFilter !== ALL_WIR
            ? item.assets?.includes(assetTypeFilter as AssetType) ||
              item.assets?.includes(ALL_WIR as AssetType)
            : item,
        )
        .filter(item =>
          roleTypeFilter !== NOT_APPLICABLE
            ? item.roles?.includes(roleTypeFilter as WIRTransverseRoles)
            : item,
        )
        .sort((itemA, itemB) => (itemA.userName ?? '').localeCompare(itemB.userName ?? '')),
    [allWirUsers, assetTypeFilter, roleTypeFilter],
  );

  useEffect(() => setPage(1), [allWirUsersFiltered]);

  useEffect(() => {
    if (page) {
      setWirUsersForPage([]);
      const itemStart = (page - 1) * WIR_USERS_PAGE_SIZE;
      const itemEnd = itemStart + WIR_USERS_PAGE_SIZE;
      const wirUserForCurrentPage = allWirUsersFiltered.slice(itemStart, itemEnd);
      setWirUsersForPage(wirUserForCurrentPage);
    }
  }, [page, allWirUsersFiltered]);

  const getPageSize = () => {
    return Math.ceil(allWirUsersFiltered.length / WIR_USERS_PAGE_SIZE);
  };

  const saverUser = useCallback(async () => {
    const userResponse =
      mode === 'add'
        ? await wellGuardianApiClient.createWIRUser(user, countryData)
        : await wellGuardianApiClient.editWIRUser(user, countryData);
    if (userResponse?.userId) {
      toast.success(
        intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.userSavedSuccessfully' }),
      );
    } else {
      toast.error(intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.errorSavingUser' }));
    }
    setOpenUserDetailsModal(false);
    fetchUsersList();
  }, [mode, user, fetchUsersList, intl, countryData]);

  const deleteUser = useCallback(async () => {
    if (user.id) {
      const userResponse = user && (await wellGuardianApiClient.deleteWIRUser(user.id));
      if (userResponse) {
        toast.success(
          intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.userRemovedSuccessfully' }),
        );
      } else {
        toast.error(
          toast.error(
            intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.errorRemovingUser' }),
          ),
        );
      }
    }
    setOpenRemoveUserConfirmationModal(false);
    fetchUsersList();
  }, [user, fetchUsersList, intl]);

  return {
    openUserRoleSettingsModal,
    setOpenUserRoleSettingsModal,
    wirUsersForPage,
    page,
    setPage,
    user,
    setUser,
    mode,
    setMode,
    openUserDetailsModal,
    setOpenUserDetailsModal,
    openRemoveUserConfirmationModal,
    setOpenRemoveUserConfirmationModal,
    saverUser,
    deleteUser,
    usersLoading,
    assetTypeFilter,
    setAssetTypeFilter,
    roleTypeFilter,
    setRoleTypeFilter,
    allWirUsers: allWirUsersFiltered,
    pageSize: getPageSize(),
  };
};
